import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FullscreenService {
  private fullscreenElementSubject = new BehaviorSubject<Element | null>(null);
  public fullscreenElement$: Observable<Element | null> = this.fullscreenElementSubject.asObservable();

  constructor() {
    // Listen to fullscreen change events
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'msfullscreenchange'
    ];

    merge(...events.map(event => fromEvent(document, event)))
      .pipe(map(() => this.getCurrentFullscreenElement()))
      .subscribe(element => this.fullscreenElementSubject.next(element));
  }

  /**
   * Request fullscreen for an element
   */
  async enterFullscreen(element: HTMLElement): Promise<void> {
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  }

  /**
   * Exit fullscreen
   */
  async exitFullscreen(): Promise<void> {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  }

  /**
   * Toggle fullscreen for an element
   */
  async toggleFullscreen(element: HTMLElement): Promise<void> {
    const currentFullscreenElement = this.getCurrentFullscreenElement();
    
    if (currentFullscreenElement === element) {
      await this.exitFullscreen();
    } else {
      if (currentFullscreenElement) {
        await this.exitFullscreen();
      }
      await this.enterFullscreen(element);
    }
  }

  /**
   * Check if an element is currently in fullscreen
   */
  isFullscreen(element: HTMLElement): boolean {
    return this.getCurrentFullscreenElement() === element;
  }

  /**
   * Check if fullscreen is supported
   */
  isSupported(): boolean {
    return !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  }

  /**
   * Get the current fullscreen element
   */
  private getCurrentFullscreenElement(): Element | null {
    return (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement ||
      null
    );
  }
}
